import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Download,
  Storage,
  TableChart,
  Business,
  LocalShipping,
  People,
  ListAlt,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

function BackupPage() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    loadSummary();
  }, []);

  const loadSummary = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/backup/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(response.data.data);
    } catch (err) {
      console.error('백업 요약 로드 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadJSON = async () => {
    setDownloading('json');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/backup/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `nara_backup_${new Date().toISOString().slice(0,10)}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('다운로드에 실패했습니다.');
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadCSV = async (table) => {
    setDownloading(table);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/backup/export/${table}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${table}_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('다운로드에 실패했습니다.');
    } finally {
      setDownloading(null);
    }
  };

  const tables = [
    { name: 'orders', label: '주문', icon: <ListAlt /> },
    { name: 'drivers', label: '운전자', icon: <LocalShipping /> },
    { name: 'clients', label: '업체', icon: <Business /> },
    { name: 'admins', label: '관리자', icon: <People /> },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        데이터 백업
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        이 기능은 최고관리자만 사용할 수 있습니다. 정기적인 백업을 권장합니다.
      </Alert>

      {/* 전체 백업 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <Storage sx={{ fontSize: 40, color: '#1976d2' }} />
              <Box>
                <Typography variant="h6">전체 데이터 백업</Typography>
                <Typography variant="body2" color="textSecondary">
                  모든 테이블의 데이터를 JSON 형식으로 다운로드합니다.
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={downloading === 'json' ? <CircularProgress size={20} color="inherit" /> : <Download />}
              onClick={handleDownloadJSON}
              disabled={downloading !== null}
            >
              JSON 다운로드
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* 데이터 현황 */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
        데이터 현황
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {tables.map((table) => (
          <Grid item xs={6} sm={3} key={table.name}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                {table.icon}
                <Typography variant="h4" fontWeight={700} sx={{ my: 1 }}>
                  {summary?.[table.name] || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {table.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* 테이블별 백업 */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
        테이블별 CSV 백업
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell>테이블</TableCell>
              <TableCell align="center">레코드 수</TableCell>
              <TableCell align="right">다운로드</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tables.map((table) => (
              <TableRow key={table.name} hover>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    {table.icon}
                    <Typography>{table.label}</Typography>
                  </Box>
                </TableCell>
                <TableCell align="center">{summary?.[table.name] || 0}건</TableCell>
                <TableCell align="right">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={downloading === table.name ? <CircularProgress size={16} /> : <TableChart />}
                    onClick={() => handleDownloadCSV(table.name)}
                    disabled={downloading !== null || (summary?.[table.name] || 0) === 0}
                  >
                    CSV
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default BackupPage;
